import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { CalendarIcon, Download, Sparkles } from 'lucide-react';
import { format, subDays, subMonths, subYears, startOfYear, endOfYear } from 'date-fns';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface UsageMetrics {
  totalUsers: number;
  activeUsers: number;
  totalCourses: number;
  completedCourses: number;
  totalEnrollments: number;
  totalMeetings: number;
  totalSurveys: number;
  totalBadges: number;
  totalVideos: number;
}

interface DistrictMetrics {
  district_name: string;
  total_users: number;
  active_users: number;
  courses_created: number;
  enrollments: number;
  completions: number;
  engagement_score: number;
}

interface FeatureUsage {
  feature: string;
  usage_count: number;
  unique_users: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

export default function DistrictUsageAnalytics() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<{ from: Date; to: Date }>({
    from: startOfYear(new Date()),
    to: endOfYear(new Date()),
  });
  const [metrics, setMetrics] = useState<UsageMetrics | null>(null);
  const [districtData, setDistrictData] = useState<DistrictMetrics[]>([]);
  const [featureUsage, setFeatureUsage] = useState<FeatureUsage[]>([]);
  const [activityTimeline, setActivityTimeline] = useState<any[]>([]);
  const [districts, setDistricts] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('all');

  useEffect(() => {
    if (user?.email === 'matt.monjan@infusedu.com' || user?.email === 'matt.monjan@infusesdu.com') {
      loadDistricts();
    }
  }, [user]);

  useEffect(() => {
    if (user?.email === 'matt.monjan@infusedu.com' || user?.email === 'matt.monjan@infusesdu.com') {
      loadAnalytics();
    }
  }, [user, dateRange, selectedDistrictId]);

  const loadDistricts = async () => {
    const { data } = await supabase
      .from('districts')
      .select('id, name')
      .order('name');
    
    if (data) {
      setDistricts(data);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadOverviewMetrics(),
        loadDistrictMetrics(),
        loadFeatureUsage(),
        loadActivityTimeline(),
      ]);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const loadOverviewMetrics = async () => {
    // Get profiles - fetch all and filter in memory
    const { data: allProfiles } = await supabase
      .from('profiles')
      .select('id, district_id');
    
    const profiles = selectedDistrictId !== 'all' 
      ? allProfiles?.filter(p => p.district_id === selectedDistrictId) 
      : allProfiles;

    const userIds = profiles?.map(p => p.id) || [];
    
    // Get courses - fetch with date filter, then filter by district if needed
    const { data: allCourses } = await supabase
      .from('courses')
      .select('id, status, created_by')
      .gte('created_at', dateRange.from.toISOString())
      .lte('created_at', dateRange.to.toISOString());
    
    const courses = selectedDistrictId !== 'all' && userIds.length > 0
      ? allCourses?.filter(c => userIds.includes(c.created_by))
      : allCourses;

    // Get enrollments
    const { data: allEnrollments } = await supabase
      .from('enrollments')
      .select('id, status, participant_id')
      .gte('enrollment_date', dateRange.from.toISOString())
      .lte('enrollment_date', dateRange.to.toISOString());
    
    const enrollments = selectedDistrictId !== 'all' && userIds.length > 0
      ? allEnrollments?.filter(e => userIds.includes(e.participant_id))
      : allEnrollments;

    // Get meetings
    const { data: allMeetings } = await supabase
      .from('meetings')
      .select('id, observer_id')
      .gte('created_at', dateRange.from.toISOString())
      .lte('created_at', dateRange.to.toISOString());
    
    const meetings = selectedDistrictId !== 'all' && userIds.length > 0
      ? allMeetings?.filter(m => userIds.includes(m.observer_id))
      : allMeetings;

    // Get surveys
    const { data: surveys } = await supabase
      .from('surveys')
      .select('id')
      .gte('created_at', dateRange.from.toISOString())
      .lte('created_at', dateRange.to.toISOString());

    // Get badges
    const { data: allBadges } = await supabase
      .from('user_badges')
      .select('id, user_id')
      .gte('awarded_at', dateRange.from.toISOString())
      .lte('awarded_at', dateRange.to.toISOString());
    
    const badges = selectedDistrictId !== 'all' && userIds.length > 0
      ? allBadges?.filter(b => userIds.includes(b.user_id))
      : allBadges;

    // Get videos (note: videos table doesn't have user_id, so we can't filter by district)
    const { data: videos } = await supabase
      .from('videos')
      .select('id')
      .gte('created_at', dateRange.from.toISOString())
      .lte('created_at', dateRange.to.toISOString());

    // Get active users from activity logs
    const { data: allActivity } = await supabase
      .from('activity_logs')
      .select('user_id')
      .gte('created_at', subDays(new Date(), 30).toISOString());
    
    const recentActivity = selectedDistrictId !== 'all' && userIds.length > 0
      ? allActivity?.filter(a => userIds.includes(a.user_id))
      : allActivity;
    
    const activeUsers = new Set(recentActivity?.map(a => a.user_id)).size || 0;

    setMetrics({
      totalUsers: profiles?.length || 0,
      activeUsers,
      totalCourses: courses?.length || 0,
      completedCourses: enrollments?.filter(e => e.status === 'completed').length || 0,
      totalEnrollments: enrollments?.length || 0,
      totalMeetings: meetings?.length || 0,
      totalSurveys: surveys?.length || 0,
      totalBadges: badges?.length || 0,
      totalVideos: videos?.length || 0,
    });
  };

  const loadDistrictMetrics = async () => {
    let districtsResult;
    if (selectedDistrictId !== 'all') {
      districtsResult = await supabase.from('districts').select('*').eq('id', selectedDistrictId);
    } else {
      districtsResult = await supabase.from('districts').select('*');
    }
    const districts = districtsResult.data;

    if (!districts) return;

    const districtMetrics = await Promise.all(
      districts.map(async (district) => {
        const { data: users } = await supabase
          .from('profiles')
          .select('id')
          .eq('district_id', district.id);

        const { data: courses } = await supabase
          .from('courses')
          .select('id, created_by')
          .gte('created_at', dateRange.from.toISOString())
          .lte('created_at', dateRange.to.toISOString());

        const districtUserIds = users?.map(u => u.id) || [];
        const districtCourses = courses?.filter(c => districtUserIds.includes(c.created_by)) || [];

        const { data: enrollments } = await supabase
          .from('enrollments')
          .select('id, status, participant_id')
          .in('participant_id', districtUserIds)
          .gte('enrollment_date', dateRange.from.toISOString())
          .lte('enrollment_date', dateRange.to.toISOString());

        const { data: districtActivity } = await supabase
          .from('activity_logs')
          .select('user_id')
          .in('user_id', users?.map(u => u.id) || [])
          .gte('created_at', subDays(new Date(), 30).toISOString());
        
        const activeUsers = new Set(districtActivity?.map(a => a.user_id)).size || 0;

        const completions = enrollments?.filter(e => e.status === 'completed').length || 0;
        const engagementScore = users && users.length > 0 
          ? Math.round((activeUsers / users.length) * 100)
          : 0;

        return {
          district_name: district.name,
          total_users: users?.length || 0,
          active_users: activeUsers,
          courses_created: districtCourses.length,
          enrollments: enrollments?.length || 0,
          completions,
          engagement_score: engagementScore,
        };
      })
    );

    setDistrictData(districtMetrics.filter(d => d.total_users > 0));
  };

  const loadFeatureUsage = async () => {
    const { data: activityLogs } = await supabase
      .from('activity_logs')
      .select('action, user_id, resource_type')
      .gte('created_at', dateRange.from.toISOString())
      .lte('created_at', dateRange.to.toISOString());

    if (!activityLogs) return;

    const featureMap = new Map<string, { count: number; users: Set<string> }>();

    activityLogs.forEach(log => {
      const feature = log.resource_type || log.action;
      if (!featureMap.has(feature)) {
        featureMap.set(feature, { count: 0, users: new Set() });
      }
      const data = featureMap.get(feature)!;
      data.count++;
      if (log.user_id) data.users.add(log.user_id);
    });

    const features: FeatureUsage[] = Array.from(featureMap.entries()).map(([feature, data]) => ({
      feature,
      usage_count: data.count,
      unique_users: data.users.size,
    })).sort((a, b) => b.usage_count - a.usage_count).slice(0, 10);

    setFeatureUsage(features);
  };

  const loadActivityTimeline = async () => {
    const months = [];
    const currentDate = new Date(dateRange.to);
    const startDate = new Date(dateRange.from);

    while (currentDate >= startDate) {
      months.unshift({
        month: format(currentDate, 'MMM yyyy'),
        date: new Date(currentDate),
      });
      currentDate.setMonth(currentDate.getMonth() - 1);
    }

    const timeline = await Promise.all(
      months.map(async ({ month, date }) => {
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        const { data: enrollments } = await supabase
          .from('enrollments')
          .select('id')
          .gte('enrollment_date', monthStart.toISOString())
          .lte('enrollment_date', monthEnd.toISOString());

        const { data: courses } = await supabase
          .from('courses')
          .select('id')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString());

        const { data: meetings } = await supabase
          .from('meetings')
          .select('id')
          .gte('created_at', monthStart.toISOString())
          .lte('created_at', monthEnd.toISOString());

        return {
          month,
          enrollments: enrollments?.length || 0,
          courses: courses?.length || 0,
          meetings: meetings?.length || 0,
        };
      })
    );

    setActivityTimeline(timeline);
  };

  const setQuickRange = (days: number) => {
    setDateRange({
      from: subDays(new Date(), days),
      to: new Date(),
    });
  };

  const setYearlyRange = () => {
    setDateRange({
      from: startOfYear(new Date()),
      to: endOfYear(new Date()),
    });
  };

  const exportData = async () => {
    const reportData = {
      dateRange: {
        from: format(dateRange.from, 'PPP'),
        to: format(dateRange.to, 'PPP'),
      },
      metrics,
      districtData,
      featureUsage,
      activityTimeline,
    };

    const dataStr = JSON.stringify(reportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `edugogy-usage-report-${format(new Date(), 'yyyy-MM-dd')}.json`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  if (user?.email !== 'matt.monjan@infusedu.com' && user?.email !== 'matt.monjan@infusesdu.com') {
    return (
      <div className="container mx-auto py-8">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>This page is only accessible to authorized administrators.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">District Usage Analytics</h1>
          <p className="text-muted-foreground">Comprehensive insights into platform engagement and feature adoption</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportData}>
            <Download className="mr-2 h-4 w-4" />
            Export Report
          </Button>
          <Button onClick={() => window.location.href = '/yearly-wrap-up'}>
            <Sparkles className="mr-2 h-4 w-4" />
            Yearly Wrap Up
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter analytics by district and date range</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>District</Label>
            <Select value={selectedDistrictId} onValueChange={setSelectedDistrictId}>
              <SelectTrigger className="w-full md:w-[300px]">
                <SelectValue placeholder="Select a district" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Districts</SelectItem>
                {districts.map((district) => (
                  <SelectItem key={district.id} value={district.id}>
                    {district.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setQuickRange(7)}>
            Last 7 Days
          </Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange(30)}>
            Last 30 Days
          </Button>
          <Button variant="outline" size="sm" onClick={() => setQuickRange(90)}>
            Last 90 Days
          </Button>
          <Button variant="outline" size="sm" onClick={setYearlyRange}>
            This Year
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {format(dateRange.from, 'PP')} - {format(dateRange.to, 'PP')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="range"
                selected={{ from: dateRange.from, to: dateRange.to }}
                onSelect={(range) => range?.from && range?.to && setDateRange({ from: range.from, to: range.to })}
                numberOfMonths={2}
                className="pointer-events-auto"
              />
            </PopoverContent>
          </Popover>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Loading analytics data...</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="districts">Districts</TabsTrigger>
            <TabsTrigger value="features">Features</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{metrics?.totalUsers}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {metrics?.activeUsers} active (last 30 days)
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Courses</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{metrics?.totalCourses}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {metrics?.completedCourses} completed
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Enrollments</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{metrics?.totalEnrollments}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    In selected period
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Meetings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{metrics?.totalMeetings}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Scheduled/completed
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Surveys</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{metrics?.totalSurveys}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Created
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Badges</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{metrics?.totalBadges}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Awarded
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Videos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{metrics?.totalVideos}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Uploaded
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="districts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>District Engagement</CardTitle>
                <CardDescription>Compare engagement metrics across districts</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={districtData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="district_name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total_users" fill="#8884d8" name="Total Users" />
                    <Bar dataKey="active_users" fill="#82ca9d" name="Active Users" />
                    <Bar dataKey="enrollments" fill="#ffc658" name="Enrollments" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Engagement Scores</CardTitle>
                <CardDescription>Active user percentage by district</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={districtData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="district_name" type="category" width={150} />
                    <Tooltip />
                    <Bar dataKey="engagement_score" fill="#0088FE" name="Engagement %" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="features" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Top Features by Usage</CardTitle>
                  <CardDescription>Most frequently used platform features</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={featureUsage}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="feature" angle={-45} textAnchor="end" height={100} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="usage_count" fill="#8884d8" name="Total Uses" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Feature Distribution</CardTitle>
                  <CardDescription>Usage breakdown by feature</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <PieChart>
                      <Pie
                        data={featureUsage}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => entry.feature}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="usage_count"
                      >
                        {featureUsage.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Activity Timeline</CardTitle>
                <CardDescription>Platform activity trends over time</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={activityTimeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="enrollments" stroke="#8884d8" name="Enrollments" />
                    <Line type="monotone" dataKey="courses" stroke="#82ca9d" name="Courses" />
                    <Line type="monotone" dataKey="meetings" stroke="#ffc658" name="Meetings" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
